/**
 * Strings of the visit views (list cards, map, day, week, team, detail panel, planning), merged into `visitsPage.*` by `visits.ts`.
 * `visitsViewsEn` is typed against `visitsViewsPl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const visitsViewsPl = {
  view: { list: 'Lista', map: 'Mapa', day: 'Dzień', week: 'Tydzień', team: 'Zespół', me: 'Moje wyniki' },
  card: {
    today: 'Dziś',
    yesterday: 'Wczoraj',
    tomorrow: 'Jutro',
    plan: 'plan',
    signed: 'Podpis',
    survey: 'Ankieta',
    distance: '{m} m od klienta',
    dayCount: 'Wizyt: {count}',
    dayDone: 'zakończonych: {count}',
    dayTime: 'u klientów {time}',
  },
  flag: { SHORT: 'Za krótka', NIGHT: 'Nocna', OUTSIDE_GEOFENCE: 'Poza strefą', NO_SIGNATURE: 'Bez podpisu' },
  review: {
    filter: 'Do sprawdzenia',
    hint: 'Za krótkie, nocne, poza strefą klienta albo zakończone bez podpisu',
    empty: 'Nic do sprawdzenia w tym okresie — wszystkie wizyty wyglądają poprawnie.',
  },
  nav: { prev: 'Poprzedni', next: 'Następny', today: 'Dziś' },
  map: {
    count: 'Wizyt: {count}',
    noCoords: 'bez współrzędnych klienta: {count}',
    truncated: 'Pokazano pierwsze 1000 — zawęź okres lub handlowca',
    cluster: 'Wizyt w tym miejscu: {count}',
    openHint: 'kliknij, aby otworzyć',
    position: 'Tu był handlowiec',
    fit: 'Pokaż wszystkie',
    routes: 'Trasy',
    routesHint: 'Trasy po drogach widać dla jednego dnia (np. „Dziś” lub „Wczoraj”) i do 12 tras naraz',
    details: 'Szczegóły wizyty',
    roadTooltip: '{name} · {km} km · {time}',
    km: '{km} km',
    straightLine: 'Trasa orientacyjna (linia prosta) — trasowanie po drogach niedostępne',
  },
  day: {
    pick: 'Wybierz dzień',
    empty: 'Brak wizyt tego dnia',
    emptyBody: 'Zaplanuj wizytę albo wybierz inny dzień.',
    laneSummary: 'Wizyt: {count} · ✓ {done}',
    collision: 'Nakładające się terminy: {count}',
    gap: 'przerwa {min} min',
    idle: 'Bez wizyt tego dnia:',
  },
  week: {
    hint: 'Przeciągnij zaplanowaną lub nieodbytą wizytę na inny dzień, aby zmienić termin — godzina zostaje.',
    empty: 'Brak wizyt',
    drop: 'Upuść tutaj',
    moved: 'Wizyta przeniesiona na {when}',
    moveFailed: 'Nie udało się przenieść wizyty',
    pastDay: 'Nie można przenieść wizyty na miniony dzień',
    duplicate: 'Na ten dzień jest już zaplanowana wizyta u tego klienta',
    planOn: 'Zaplanuj wizytę na ten dzień',
  },
  plan: {
    button: 'Zaplanuj wizytę',
    title: 'Zaplanuj wizytę',
    desc: 'Wizyta pojawi się handlowcowi w aplikacji w „Moim dniu”.',
    titleMove: 'Zmień termin wizyty',
    descMove: 'Zmień dzień i godzinę albo przekaż wizytę innemu handlowcowi.',
    customerPlaceholder: 'Szukaj klienta (min. 2 znaki)…',
    day: 'Dzień',
    time: 'Godzina',
    notes: 'Notatka',
    notesPlaceholder: 'Np. omówić nową ofertę, odebrać zwroty…',
    me: 'Ja',
    save: 'Zaplanuj',
    saveMove: 'Zapisz termin',
    saved: 'Wizyta zaplanowana',
    moved: 'Termin zmieniony',
    errors: {
      customer: 'Wybierz klienta',
      when: 'Podaj dzień i godzinę',
      past: 'Nie można planować wizyty w przeszłości',
      duplicate: 'Na ten dzień jest już zaplanowana wizyta u tego klienta',
      notFound: 'Klient lub handlowiec jest poza Twoim zakresem',
      generic: 'Nie udało się zapisać — spróbuj ponownie',
    },
  },
  drawer: {
    place: 'Miejsce wizyty',
    noCoords: 'Klient nie ma zapisanych współrzędnych.',
    noPosition: 'Brak zapisanej pozycji handlowca (wizyta wpisana ręcznie albo bez GPS).',
    geofence: 'Pozycja z początku wizyty; strefa klienta: {radius} m.',
    delay: '({min} min względem planu)',
    photos: 'Zdjęcia',
    noPhotos: 'Brak zdjęć.',
    photosError: 'Nie udało się wczytać zdjęć.',
    photoOf: 'Zdjęcie {n} z {total}',
    prev: 'Poprzednie',
    next: 'Następne',
    contacts: 'Osoby kontaktowe',
    call: 'Zadzwoń',
    navigate: 'Nawiguj',
    task: 'Zadanie po wizycie',
    protocol: 'Protokół (PDF)',
    move: 'Zmień termin',
    cancel: 'Anuluj wizytę',
    cancelAsk: 'Anulować tę wizytę?',
    cancelNo: 'Nie',
    cancelYes: 'Tak, anuluj',
    cancelled: 'Wizyta anulowana',
    flagHint: {
      SHORT: 'Wizyta krótsza niż 5 minut.',
      NIGHT: 'Rozpoczęta lub zakończona poza godzinami 5:00–21:00.',
      OUTSIDE_GEOFENCE: 'Rozpoczęta {m} m od klienta (strefa {radius} m).',
      NO_SIGNATURE: 'Zakończona bez podpisu klienta.',
    },
  },
  protocol: { title: 'Protokół wizyty', generated: 'Wygenerowano {date}' },
  team: {
    heatmap: 'Kiedy odbywają się wizyty',
    heatCell: 'Zakończonych wizyt: {count} (godz. {hour})',
    completed: 'Zakończone wizyty',
    ofAll: 'z {total} wszystkich',
    avgTime: 'Średni czas wizyty',
    perDay: 'Dziennie',
    perDayHint: 'Średnio {count} na dzień pracy',
    customers: 'Klientów',
    signed: 'Podpis',
    signedHint: 'Bez podpisu: {count}',
    photos: 'Zdjęcia',
    survey: 'Ankieta',
    flags: 'Do sprawdzenia',
    flagsHint: 'Poza strefą {radius} m, za krótkie, nocne, bez podpisu',
    activeDays: 'Dni z wizytami: {count}',
    show: 'Wizyty',
    review: 'Sprawdź',
    clean: 'Bez uwag',
    coverage: 'Pokrycie klientów (30 dni)',
    overdue: 'bez wizyty: {count} z {total}',
    flag: {
      outside: 'Poza strefą: {count}',
      short: 'Za krótkie: {count}',
      night: 'Nocne: {count}',
      unsigned: 'Bez podpisu: {count}',
      missed: 'Nieodbyte: {count}',
    },
    empty: 'Brak handlowców w zakresie',
    emptyBody: 'Nie ma osób, dla których można pokazać wyniki wizyt.',
  },
} as const;

export const visitsViewsEn: Shape<typeof visitsViewsPl> = {
  view: { list: 'List', map: 'Map', day: 'Day', week: 'Week', team: 'Team', me: 'My results' },
  card: {
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    plan: 'planned',
    signed: 'Signed',
    survey: 'Survey',
    distance: '{m} m from the customer',
    dayCount: 'Visits: {count}',
    dayDone: 'completed: {count}',
    dayTime: '{time} at customers',
  },
  flag: { SHORT: 'Too short', NIGHT: 'Night', OUTSIDE_GEOFENCE: 'Outside zone', NO_SIGNATURE: 'Unsigned' },
  review: {
    filter: 'To check',
    hint: 'Too short, at night, outside the customer zone or completed without a signature',
    empty: 'Nothing to check in this period — all visits look fine.',
  },
  nav: { prev: 'Previous', next: 'Next', today: 'Today' },
  map: {
    count: 'Visits: {count}',
    noCoords: 'without customer coordinates: {count}',
    truncated: 'Showing the first 1000 — narrow the period or the rep',
    cluster: 'Visits here: {count}',
    openHint: 'click to open',
    position: 'The rep was here',
    fit: 'Show all',
    routes: 'Routes',
    routesHint: 'Routes show for a single day (e.g. “Today” or “Yesterday”) and up to 12 at a time',
    details: 'Visit details',
    roadTooltip: '{name} · {km} km · {time}',
    km: '{km} km',
    straightLine: 'Approximate route (straight line) — road routing unavailable',
  },
  day: {
    pick: 'Pick a day',
    empty: 'No visits that day',
    emptyBody: 'Plan a visit or pick another day.',
    laneSummary: 'Visits: {count} · ✓ {done}',
    collision: 'Overlapping visits: {count}',
    gap: '{min} min gap',
    idle: 'No visits that day:',
  },
  week: {
    hint: 'Drag a planned or missed visit to another day to reschedule it — the time stays.',
    empty: 'No visits',
    drop: 'Drop here',
    moved: 'Visit moved to {when}',
    moveFailed: 'Could not move the visit',
    pastDay: 'A visit cannot be moved to a past day',
    duplicate: 'A visit to this customer is already planned for that day',
    planOn: 'Plan a visit on this day',
  },
  plan: {
    button: 'Plan a visit',
    title: 'Plan a visit',
    desc: 'The visit shows up in the rep’s “My day” in the app.',
    titleMove: 'Reschedule the visit',
    descMove: 'Change the day and time or hand the visit over to another rep.',
    customerPlaceholder: 'Search customer (min. 2 characters)…',
    day: 'Day',
    time: 'Time',
    notes: 'Note',
    notesPlaceholder: 'E.g. discuss the new offer, collect returns…',
    me: 'Me',
    save: 'Plan',
    saveMove: 'Save the date',
    saved: 'Visit planned',
    moved: 'Date changed',
    errors: {
      customer: 'Pick a customer',
      when: 'Enter the day and time',
      past: 'A visit cannot be planned in the past',
      duplicate: 'A visit to this customer is already planned for that day',
      notFound: 'The customer or rep is outside your scope',
      generic: 'Could not save — try again',
    },
  },
  drawer: {
    place: 'Where the visit took place',
    noCoords: 'The customer has no coordinates saved.',
    noPosition: 'No rep position was saved (manual visit or no GPS).',
    geofence: 'Position at the start of the visit; customer zone: {radius} m.',
    delay: '({min} min vs. plan)',
    photos: 'Photos',
    noPhotos: 'No photos.',
    photosError: 'Could not load the photos.',
    photoOf: 'Photo {n} of {total}',
    prev: 'Previous',
    next: 'Next',
    contacts: 'Contacts',
    call: 'Call',
    navigate: 'Navigate',
    task: 'Follow-up task',
    protocol: 'Protocol (PDF)',
    move: 'Reschedule',
    cancel: 'Cancel visit',
    cancelAsk: 'Cancel this visit?',
    cancelNo: 'No',
    cancelYes: 'Yes, cancel',
    cancelled: 'Visit cancelled',
    flagHint: {
      SHORT: 'The visit lasted under 5 minutes.',
      NIGHT: 'Started or ended outside 5:00–21:00.',
      OUTSIDE_GEOFENCE: 'Started {m} m from the customer (zone {radius} m).',
      NO_SIGNATURE: 'Completed without the customer’s signature.',
    },
  },
  protocol: { title: 'Visit protocol', generated: 'Generated {date}' },
  team: {
    heatmap: 'When visits happen',
    heatCell: 'Completed visits: {count} ({hour})',
    completed: 'Completed visits',
    ofAll: 'of {total} in total',
    avgTime: 'Average visit time',
    perDay: 'Per day',
    perDayHint: '{count} per working day on average',
    customers: 'Customers',
    signed: 'Signature',
    signedHint: 'Unsigned: {count}',
    photos: 'Photos',
    survey: 'Survey',
    flags: 'To check',
    flagsHint: 'Outside {radius} m zone, too short, at night, unsigned',
    activeDays: 'Days with visits: {count}',
    show: 'Visits',
    review: 'Check',
    clean: 'No remarks',
    coverage: 'Customer coverage (30 days)',
    overdue: 'not visited: {count} of {total}',
    flag: {
      outside: 'Outside zone: {count}',
      short: 'Too short: {count}',
      night: 'At night: {count}',
      unsigned: 'Unsigned: {count}',
      missed: 'Missed: {count}',
    },
    empty: 'No reps in scope',
    emptyBody: 'There is nobody to show visit results for.',
  },
};
